import React, { useEffect, useMemo, useRef } from "react";
import Calendar from "react-calendar";
import {
  string1ToDateObject,
  stringToDateObject,
} from "../../utils/Formater/DateFormat";
import moment from "moment";
import NextIcon from "../../icons/Calander/CalenderNext";
import PrevIcon from "../../icons/Calander/CalenderPrev";
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";

interface CalanderProps {
  isRightSide?: boolean;
  value?: any;
  onChange?: any;
  minDate?: any;
  maxDate?: any;
  disableTiles?: any;
  isDashFormat?: any;
  className?: string;
  openCalendar?: boolean | false;
  setOpenCalendar?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CustomCalender = ({
  isRightSide = false,
  value,
  onChange,
  minDate,
  maxDate,
  disableTiles,
  isDashFormat,
  className = "",
  setOpenCalendar,
  openCalendar,
}: CalanderProps) => {
  const { generalSettings } = useAppSelector(
    (state: RootState) => state.payment
  );
  const { holidayList } = useAppSelector((state: RootState) => state.dashboard);
  const calenderRef = useRef<HTMLDivElement | null>(null);

  const isWeekend = (date: any) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };

  const isIncludeHoliday = (date: any) => {
    return holidayList?.some(
      (holiday: any) =>
        date.getDate() === new Date(holiday.date).getDate() &&
        date.getMonth() === new Date(holiday.date).getMonth() &&
        date.getFullYear() === new Date(holiday.date).getFullYear()
    );
  };

  const isDateDisabled = (date: any) => {
    // Check if the date is in the disabledDates array
    const formatted = disableTiles?.map((itm: any) => {
      return isDashFormat
        ? string1ToDateObject(itm.date)
        : stringToDateObject(itm);
    });
    return (
      formatted?.some(
        (disabledDate: any) =>
          date.getDate() === disabledDate.getDate() &&
          date.getMonth() === disabledDate.getMonth() &&
          date.getFullYear() === disabledDate.getFullYear()
      ) ||
      (generalSettings?.systemSettings?.off_days_payments === "N" &&
        isWeekend(date))
    );
  };

  const getMinDate = (): Date | undefined => {
    if (minDate) return minDate;
    return undefined;
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calenderRef.current &&
        !calenderRef.current.contains(event.target as Node)
      ) {
        setOpenCalendar?.(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedDate = useMemo(() => {
    if (!value) return value;
    if (value instanceof Date) return value;
    if (Array.isArray(value)) {
      return value.map((item) => (item instanceof Date ? item : new Date(item)));
    }

    const formattedValue = String(value);
    const parsedMoment = moment(
      formattedValue,
      ["MM/DD/YYYY", "YYYY-MM-DD", moment.ISO_8601],
      true
    );
    const parsedDate = parsedMoment.isValid()
      ? parsedMoment.toDate()
      : isDashFormat
        ? string1ToDateObject(value)
        : stringToDateObject(value);

    return Number.isNaN(parsedDate?.getTime?.()) ? new Date(value) : parsedDate;
  }, [isDashFormat, value]);

  if (!openCalendar) return null;

  return (
    <div
      ref={calenderRef}
      className={`customCalender absolute top-[100%] ${
        isRightSide ? "right-0" : "left-0"
      } w-[268px] h-[264px] z-[90] ${className}`}
    >
      <Calendar
        calendarType="US"
        className="!border-none text-txtPrimary rounded-[7px] text-sm p-[18px] shadow-[0px_5px_15px_rgba(60,66,87,0.2)]"
        onChange={(val) => {
          onChange?.(val);
          setOpenCalendar?.(false); // optional: close after selecting
        }}
        value={selectedDate}
        minDate={getMinDate()}
        maxDate={maxDate}
        // react-calendar reuses this for the month and year tiles, where `date`
        // is the 1st of the period — without the view guard a whole month or
        // year is greyed out whenever it happens to start on a weekend.
        tileDisabled={({ date, view }) =>
          view === "month" && disableTiles && isDateDisabled(date)
        }
        tileClassName={({ date, view }) => {
          if (
            view === "month" &&
            (date.getDay() === 6 || date.getDay() === 0)
          ) {
            return "text-txtPrimary";
          }
          return null;
        }}
        formatShortWeekday={(locale, date) =>
          date.toLocaleDateString(locale, { weekday: "short" }).slice(0, 2)
        }
        prevLabel={
          <div className="flex justify-center items-center">
            <PrevIcon
              className="group cursor-pointer"
              _className="group-hover:!fill-iconObscure"
            />
          </div>
        }
        nextLabel={
          <div className="flex justify-center items-center">
            <NextIcon
              className="group cursor-pointer"
              _className="group-hover:!fill-iconObscure"
            />
          </div>
        }
      />
    </div>
  );
};

export default CustomCalender;

